import type { Card, SourceRef } from "./types";

export function sourceLabel(ref: SourceRef): string {
  const bits: string[] = [ref.document || "source"];
  if (ref.page != null) bits.push(`p.${ref.page}`);
  if (ref.ts_start != null) bits.push(timestamp(ref.ts_start));
  if (ref.heading) bits.push(`§ ${ref.heading}`);
  return bits.join(" · ");
}

export function timestamp(seconds: number): string {
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export const CARD_TYPE_LABEL: Record<string, string> = {
  basic: "Basic",
  basic_reversed: "Basic + reversed",
  cloze: "Cloze",
  image_occlusion: "Image occlusion",
};

export const STATUS_LABEL: Record<string, string> = {
  proposed: "Needs review",
  accepted: "Accepted",
  rejected: "Rejected",
  flagged: "Flagged",
  duplicate: "Duplicate",
};

/** Human-readable names for QC flag codes, so chips read as English. */
export const FLAG_LABEL: Record<string, string> = {
  supported: "Not supported by the source",
  unambiguous: "Ambiguous question",
  atomic: "Tests more than one thing",
  useful: "Low value",
  no_answer_leak: "Answer leaks in the question",
  type_appropriate: "Wrong card type",
  duplicate: "Duplicate",
  answer_in_question: "Answer is in the question",
  vague_question: "Too open-ended",
  answer_too_long: "Answer too long",
  not_a_question: "Not phrased as a question",
  cloze_stopword: "Deletion is guessable",
  cloze_ordinal_gap: "Cloze numbering has a gap",
  cloze_ordinal_start: "Cloze must start at c1",
  cloze_no_context: "Too much of the sentence is hidden",
  cloze_too_long: "Deletion is too long",
  no_cloze_deletion: "No cloze deletion",
  empty_front: "Empty front",
  empty_back: "Empty back",
  front_equals_back: "Front and back are identical",
  bad_tag: "Tag contains a space",
  unsafe_html: "Unsafe HTML",
  missing_media: "Image is missing",
  occlusion_no_shapes: "No occlusion regions",
  occlusion_no_image: "No image",
  occlusion_ordinal_gap: "Region numbering has a gap",
  occlusion_zero_area: "A region has no area",
  occlusion_out_of_bounds: "A region falls outside the image",
  merged: "Merged into another card",
  split: "Split into several cards",
};

export function flagLabel(code: string): string {
  return FLAG_LABEL[code] ?? code.replace(/_/g, " ");
}

/** A one-line preview used in lists and duplicate warnings. */
export function preview(card: Card): string {
  const strip = (s: string) => s.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (card.type === "cloze") return strip(card.cloze_text);
  if (card.type === "image_occlusion") {
    const labels = card.occlusion_shapes.map((s) => s.label).filter(Boolean).join(", ");
    return `${strip(card.header)}${labels ? ` — hides: ${labels}` : ""}`;
  }
  return strip(card.front);
}

export function confidenceTone(value: number): "high" | "medium" | "low" {
  if (value >= 0.8) return "high";
  if (value >= 0.6) return "medium";
  return "low";
}
